import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Close the drawer when the viewport grows to the desktop breakpoint (≥750px).
   * The drawer is only hidden by CSS at desktop, not closed — its lingering
   * `menu-open`/`[open]` state keeps matching the header's `:has(...menu-open)`
   * rules, which solidify the transparent header to a white bar. Combined with the
   * nav text staying white (the header keeps its `transparent` attribute), that
   * leaves white-on-white invisible nav after resizing up from mobile with the menu
   * open. Closing on the breakpoint cross clears that state so the header reverts to
   * its transparent overlay. (Switching the other way keeps working — the drawer
   * starts closed.)
   * @param {MediaQueryListEvent} event
   */
  #onBreakpointChange = (event) => {
    if (event.matches && this.isOpen) this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // Rapid toggling: a close() may have run between the click and this frame.
      // aria-expanded is set synchronously by open()/#close(), so it's the source
      // of truth for the latest intent — bail if the drawer is no longer meant to
      // be open, otherwise this deferred frame would re-show a just-closed drawer.
      if (summary.getAttribute('aria-expanded') !== 'true') return;

      // Clear any lingering close-wipe state (rapid open→close→open) so the
      // open and close wipe animations never run on the same element at once.
      details.classList.remove('menu-closing');
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    // Short-lived close state: `menu-open` is removed synchronously, so the
    // `:has(...menu-open)` open-wipe selector can't drive the close. `menu-closing`
    // lives only for the close animation and drives the reverse right→left wipe of
    // the header-bar underlay.
    details.classList.add('menu-closing');
    // Clear `menu-closing` when the underlay's reverse wipe ends. reset() (the
    // onAnimationEnd(drawer) callback below) is NOT a reliable cleanup here: on a
    // real close the native <details> toggle hides the drawer instantly, so the
    // drawer's transitionend may never fire. The underlay is a header sibling (never
    // display:none'd) so its own `animationend` always fires. Scoped to the main
    // drawer — only `.menu-drawer-container` drives the wipe. `once` self-cleans, and
    // if a rapid re-open cancels the wipe, open() has already dropped `menu-closing`.
    if (details.classList.contains('menu-drawer-container')) {
      const underlay = this.closest('#header-component')?.querySelector('.header__underlay-closed');
      underlay?.addEventListener(
        'animationend',
        () => {
          // Snap the bar's height to its resting value WITHOUT the height transition.
          // On the transparent home the closed bar height is 0px; removing
          // `menu-closing` reverts `--closed-underlay-height` 100%→0, and the base
          // `transition: height` would otherwise collapse the (now-unclipped) bar
          // bottom→top — a stray bar left over AFTER the R→L wipe. The wipe already
          // left it fully clipped, so snapping is invisible. (Non-home pages keep
          // height:100%, so this is a no-op there.)
          underlay.style.transition = 'none';
          details.classList.remove('menu-closing');
          void underlay.offsetWidth; // force reflow so the height snaps instantly
          underlay.style.transition = '';
        },
        { once: true }
      );
    }
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // Rapid toggling: if the drawer was re-opened while this close animation
        // was still running, the close transition gets cancelled and this stale
        // callback fires anyway (Animation.finished rejects → allSettled resolves).
        // aria-expanded reflects the latest intent — don't tear down a fresh open.
        if (summary.getAttribute('aria-expanded') === 'true') return;

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.classList.remove('menu-closing');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
